import React from 'react';
import {
  Dialog, Box, Typography, IconButton, Skeleton, Divider, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchRelatedNews, sendNewsEvent } from '../../services/newsApi';

interface NewsRelatedDialogProps {
  articleId: number | null;
  /** 一覧と同じ媒体フィルタ。揃えないとカードの件数と中身が食い違う */
  sources: string[];
  onClose: () => void;
}

/**
 * 同じ話題の記事をまとめて見るダイアログ。
 *
 * 「話題」タブは同じゲームの記事を1件に畳んでいる。件数だけ出しても
 * 何のことか分からないので、畳んだ中身をここで開けるようにしている。
 */
const NewsRelatedDialog: React.FC<NewsRelatedDialogProps> = ({ articleId, sources, onClose }) => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['news-related', articleId, sources.join(',')],
    queryFn: () => fetchRelatedNews(articleId as number, sources),
    enabled: articleId != null,
    staleTime: 5 * 60 * 1000,
  });

  const game = data?.game;
  const gameName = game ? game.japanese_name || game.name : null;

  return (
    <Dialog
      open={articleId != null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          // クッションと同じくスマホでは余白を詰める
          m: { xs: 1, sm: 4 },
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          maxHeight: { xs: 'calc(100% - 16px)', sm: '88vh' },
          borderRadius: 3,
          backgroundColor: 'rgba(20,20,20,0.94)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255,255,255,0.12)',
          backgroundImage: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 2.5, pt: 2, pb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 700 }}>
            この話題の記事
          </Typography>
          {/* 起点の記事も results に含まれるので、そのまま件数に使う */}
          {gameName && (
            <Typography sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' }, color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
              『{gameName}』について {data?.results.length ?? 0} 件
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <Box sx={{ maxHeight: '58vh', overflowY: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            {[...Array(4)].map((_, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <Skeleton variant="rectangular" width={80} height={45} sx={{ borderRadius: 1 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="100%" height={16} />
                  <Skeleton width="45%" height={14} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          (data?.results ?? []).map((a) => (
            <Box
              key={a.id}
              onClick={() => {
                sendNewsEvent(a.id, 'click');
                window.open(a.url, '_blank', 'noopener,noreferrer');
              }}
              sx={{
                display: 'flex',
                gap: 1.5,
                px: 2.5,
                py: 1.25,
                cursor: 'pointer',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 80,
                  aspectRatio: '16 / 9',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'rgba(255,255,255,0.05)',
                }}
              >
                {a.image ? (
                  <Box
                    component="img"
                    src={a.image.url}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%', height: '100%', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      color: 'rgba(255,255,255,0.2)',
                    }}
                  >
                    <NewspaperIcon sx={{ fontSize: 16 }} />
                  </Box>
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    fontWeight: 500,
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {a.title}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' }, color: 'rgba(255,255,255,0.45)', mt: 0.4 }}>
                  {a.source.name}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>

      {game && (
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => {
              onClose();
              navigate(`/game/${game.game_id}`);
            }}
          >
            『{gameName}』のページへ
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default NewsRelatedDialog;
